import {Box} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import React, {
  CSSProperties,
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {Outlet, useSearchParams} from "react-router-dom";
import StripeConnectBar from "view/components/StripeConnectBar";
import {STRIPE_CONNECT_BAR_HEIGHT} from "view/components/StripeConnectBar/helpers";
import AppBar, {APP_BAR_HEIGHT} from "view/components/AppBar";
import {SHOW_BAR_HEIGHT} from "view/components/AppBar/components/ShowBar/styles";
import NavBar from "view/components/SideBar";
import {
  useApplyExhibitorLanguage,
  useAutoCatchPasswordReset,
  useCurrentShowId,
  useOrganiserShows,
} from "hooks";
import useGoogleLinkedToast from "hooks/useGoogleLinkedToast";
import {useSelector} from "react-redux";
import {selectIsImpersonating, selectUser} from "store/auth/selectors";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import {useMaintenanceModeStore} from "store/general/maintenanceMode.store";
import {useImpersonationBannerStore} from "store/general/impersonationBanner.store";
import Chat from "view/components/Chat";
import {DEFAULT_OPACITY, hexToRgba} from "tools/theme/helpers";
import {BrandingType, Roles} from "@evennt-tech/api-v2-core";
import useBranding from "store/branding/branding.store";
import useViewMode, {ViewMode} from "store/viewMode/viewMode.store";
import ForceSetCountryModal from "view/apps/Organiser/pages/Show/Dashboard/components/ForceSetCountryModal";
import EmbeddedChat from "view/EmbeddedChat";
import {useEmbeddedChatStore} from "store/embedded-chat/embedded-chat.store";
import {useProductCategoryLocalizationActions} from "store/localization/product-category-localization-store";
import {useProductColorLocalizationActions} from "store/localization/product-color-localization-store";
import {useLocalizedLocalMapActions} from "store/localization/localized-local-map.store";
import {useLocalizationLanguageStore} from "store/localization/localization-language-admin.store";
import {useCurrentExhibitor} from "store/current-exhibitor/current-exhibitor.store";
import {consumeTwoFactorPromptOnLogin} from "tools/auth/twoFactorLoginPrompt";
import TwoFactorSettingsModal from "view/components/TwoFactorSettingsModal";
import ActivationPromptModal from "view/components/ActivationPromptModal";
import {useTwoFactorSettings} from "store/two-factor/two-factor-settings.store";
import {useShallow} from "zustand/react/shallow";
import useShowStripeConnectBar from "./useShowStripeConnectBar";
import useStyles from "./styles";

const Layout: FC = () => {
  const classes = useStyles();
  const [appTopEmptySpace, setAppTopEmptySpace] = useState(0);
  const showId = useCurrentShowId();
  const {role, id} = useSelector(selectUser);
  const isImpersonating = useSelector(selectIsImpersonating);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSecuritySettingsOpen, setIsSecuritySettingsOpen] = useState(false);
  const [activationPromptClosed, setActivationPromptClosed] = useState(false);
  const [justLoggedIn] = useState(consumeTwoFactorPromptOnLogin);
  const {twoFactorSettings, fetchTwoFactorSettings} = useTwoFactorSettings(
    useShallow((state) => ({
      twoFactorSettings: state.settings,
      fetchTwoFactorSettings: state.fetchSettings,
    }))
  );
  const embeddedChat = useEmbeddedChatStore((state) => state.embeddedChat);
  const {autoCatchTranslations} = useProductCategoryLocalizationActions();
  const {
    autoCatchTranslations: autoCatchColorTranslations,
  } = useProductColorLocalizationActions();
  const {autoCatchKeys} = useLocalizedLocalMapActions();
  const autoCatchLanguages = useLocalizationLanguageStore(
    (state) => state.autoCatchLanguages
  );
  const autoCatchCurrentExhibitor = useCurrentExhibitor(
    (state) => state.autoCatch
  );

  useApplyExhibitorLanguage();
  useGoogleLinkedToast();

  const exhibitorPairId = useCurrentExhibitorPairId();

  const [shows] = useOrganiserShows();

  const {mode} = useViewMode();
  const {getBranding, fetch, autoCatch} = useBranding();
  const branding = getBranding(showId);

  const isShowStripeConnectBar = useShowStripeConnectBar({shows});

  const {
    maintenanceMode,
    panelHeight,
    shouldShowMaintenanceModeNotification,
  } = useMaintenanceModeStore();
  const bannerHeight = useImpersonationBannerStore(
    (state) => state.bannerHeight
  );
  useAutoCatchPasswordReset();

  useEffect(() => {
    if (role !== Roles.ORGANISER) {
      return;
    }
    return autoCatchKeys();
  }, [role]);

  useEffect(() => {
    if (showId) {
      fetch(showId);
      autoCatch(showId);
    }
  }, [showId]);

  useEffect(() => {
    if (role !== Roles.ADMIN) return;
    const unsubColors = autoCatchColorTranslations();
    const unsubCategories = autoCatchTranslations();
    const unsubLanguages = autoCatchLanguages();

    return () => {
      unsubColors();
      unsubCategories();
      unsubLanguages();
    };
  }, [role]);

  useEffect(() => {
    if (role !== Roles.EXHIBITOR || !showId) return;

    return autoCatchCurrentExhibitor(showId);
  }, [role, showId]);

  useEffect(() => {
    if (role === Roles.ADMIN || isImpersonating) return;
    if (!searchParams.get("openSecuritySettings")) return;

    searchParams.delete("openSecuritySettings");
    setSearchParams(searchParams, {replace: true});
    setIsSecuritySettingsOpen(true);
  }, [role, isImpersonating, searchParams, setSearchParams]);

  // 2FA management is off-limits while viewing as another user — the API
  // answers 403 across the whole surface (`@DenyImpersonatedSession()`), so
  // fetching here would only produce a failed request and leave
  // `twoFactorSettings` null.
  useEffect(() => {
    if (!role || role === Roles.ADMIN || isImpersonating) return;
    fetchTwoFactorSettings();
  }, [role, isImpersonating, fetchTwoFactorSettings]);

  useEffect(() => {
    setActivationPromptClosed(false);
  }, [id]);

  const isOpeningSecuritySettings =
    isSecuritySettingsOpen || !!searchParams.get("openSecuritySettings");

  const showActivationPrompt =
    !activationPromptClosed &&
    !isOpeningSecuritySettings &&
    !!role &&
    role !== Roles.ADMIN &&
    justLoggedIn &&
    !isImpersonating &&
    !!twoFactorSettings &&
    !twoFactorSettings.enabled &&
    !twoFactorSettings.activationPromptDismissed;

  useLayoutEffect(() => {
    const shouldApplyAdditionalHeight =
      role &&
      showId &&
      ([Roles.SUPPLIER, Roles.EXHIBITOR].includes(role) ||
        mode === ViewMode.EXHIBITOR);
    if (shouldApplyAdditionalHeight) {
      const total =
        SHOW_BAR_HEIGHT +
        bannerHeight +
        (shouldShowMaintenanceModeNotification() ? panelHeight : 0);
      setAppTopEmptySpace(total);
      return;
    }
    const barHeight =
      APP_BAR_HEIGHT +
      bannerHeight +
      (shouldShowMaintenanceModeNotification() ? panelHeight : 0);
    setAppTopEmptySpace(
      isShowStripeConnectBar ? barHeight + STRIPE_CONNECT_BAR_HEIGHT : barHeight
    );
  }, [
    isShowStripeConnectBar,
    maintenanceMode.showNotification,
    maintenanceMode.description,
    maintenanceMode.enabled,
    showId,
    role,
    panelHeight,
    bannerHeight,
    shouldShowMaintenanceModeNotification,
    mode,
  ]);

  const {r, g, b, a} = hexToRgba(
    branding?.backgroundColor?.hex || "#FFFFFF",
    branding?.backgroundOpacity || DEFAULT_OPACITY
  );

  const backgroundStyles = useMemo<CSSProperties>(() => {
    if (role !== "exhibitor" && mode !== ViewMode.EXHIBITOR) {
      return {};
    }
    const transformedOpacity = !branding?.backgroundOpacity
      ? 0
      : 1 - branding.backgroundOpacity / 100;
    const opacity = `linear-gradient(rgba(0,0,0,${transformedOpacity}), rgba(0,0,0,${transformedOpacity})),`;
    const isImageBackground = branding?.backgroundType === BrandingType.IMAGE;
    return {
      backgroundImage: isImageBackground
        ? `${opacity} url("${branding?.backgroundImage?.url}")`
        : undefined,
      backgroundColor:
        branding?.backgroundType === BrandingType.COLOR
          ? `rgba(${r}, ${g}, ${b}, ${a})`
          : undefined,
      backgroundPosition: `0 ${appTopEmptySpace}px`,
      ...(isImageBackground && {backgroundAttachment: "fixed"}),
    };
  }, [
    a,
    b,
    branding?.backgroundImage?.url,
    branding?.backgroundOpacity,
    branding?.backgroundType,
    g,
    mode,
    r,
    role,
    appTopEmptySpace,
  ]);

  const shouldShift =
    !!showId && !!embeddedChat?.codeSnippet && role === Roles.EXHIBITOR;

  return (
    <div className={classes.root} style={{...backgroundStyles}}>
      <CssBaseline />
      <AppBar />
      {isSecuritySettingsOpen && (
        <TwoFactorSettingsModal
          open={isSecuritySettingsOpen}
          onClose={() => {
            setIsSecuritySettingsOpen(false);
            setActivationPromptClosed(true);
          }}
        />
      )}
      {showActivationPrompt && (
        <ActivationPromptModal
          onClose={() => setActivationPromptClosed(true)}
          onEnable={() => {
            setActivationPromptClosed(true);
            setIsSecuritySettingsOpen(true);
          }}
        />
      )}
      {isShowStripeConnectBar && <StripeConnectBar />}

      {role === "organiser" && !!showId && (
        <ForceSetCountryModal showId={showId} />
      )}

      <NavBar topSpacing={appTopEmptySpace} />
      <main
        className={classes.content}
        style={{...(shouldShift && {marginBottom: 30})}}
      >
        <Box height={appTopEmptySpace} />
        <Outlet />
        {role === Roles.EXHIBITOR && exhibitorPairId && showId && (
          <Chat
            type="bubble"
            showId={showId}
            userRole={Roles.EXHIBITOR}
            exhibitorPairId={exhibitorPairId}
          />
        )}
        <EmbeddedChat />
      </main>
    </div>
  );
};

export default Layout;
